import { useState, useEffect, useRef, useCallback } from "react";

interface ReviewSession {
  memberId: string;
  patientName: string;
  startTime: number;
  elapsedSeconds: number;
  isActive: boolean;
}

export function useRpmReviewTimer() {
  const [sessions, setSessions] = useState<Map<string, ReviewSession>>(new Map());
  const intervalsRef = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());

  const startReview = useCallback((memberId: string, patientName: string) => {
    setSessions((prev) => {
      const next = new Map(prev);
      const existing = next.get(memberId);
      if (existing?.isActive) return prev; // already running

      next.set(memberId, {
        memberId,
        patientName,
        startTime: Date.now(),
        elapsedSeconds: existing?.elapsedSeconds || 0,
        isActive: true,
      });
      return next;
    });

    // Start interval
    if (!intervalsRef.current.has(memberId)) {
      const interval = setInterval(() => {
        setSessions((prev) => {
          const session = prev.get(memberId);
          if (!session || !session.isActive) return prev;
          const next = new Map(prev);
          next.set(memberId, {
            ...session,
            elapsedSeconds: session.elapsedSeconds + 1,
          });
          return next;
        });
      }, 1000);
      intervalsRef.current.set(memberId, interval);
    }
  }, []);

  const stopReview = useCallback((memberId: string) => {
    const interval = intervalsRef.current.get(memberId);
    if (interval) {
      clearInterval(interval);
      intervalsRef.current.delete(memberId);
    }
    setSessions((prev) => {
      const session = prev.get(memberId);
      if (!session) return prev;
      const next = new Map(prev);
      next.set(memberId, { ...session, isActive: false });
      return next;
    });
  }, []);

  const resetReview = useCallback((memberId: string) => {
    stopReview(memberId);
    setSessions((prev) => {
      const next = new Map(prev);
      next.delete(memberId);
      return next;
    });
  }, [stopReview]);

  const getSession = useCallback(
    (memberId: string): ReviewSession | undefined => sessions.get(memberId),
    [sessions]
  );

  const isEligibleForBilling = useCallback(
    (memberId: string): boolean => {
      const session = sessions.get(memberId);
      return (session?.elapsedSeconds || 0) >= 20 * 60; // 20 minutes
    },
    [sessions]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      intervalsRef.current.forEach((interval) => clearInterval(interval));
      intervalsRef.current.clear();
    };
  }, []);

  return {
    sessions,
    startReview,
    stopReview,
    resetReview,
    getSession,
    isEligibleForBilling,
  };
}
